import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { createRef, useCallback, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { generatedReportsActions } from 'app-components/flux/generated-reports';
import { trackReceiptEvent } from 'app-components/flux/receipts';
import { reportConfigurationsSelectors } from 'app-components/flux/report-configurations';
import { tracker } from 'app-components/utils/core';
import type RootState from 'app-components/utils/redux-store/rootState';

import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';

import type { FeaturePage } from 'containers/App/paths';
import { makeNavigationItemId } from 'containers/App/paths';

import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { type FeatureFlagsKeys } from 'app-components/flux/feature-flags/featureFlagsTypes';
import { useHoverStore, useIsInteractive } from '../HoverContext';
import RefreshButton from '../RefreshButton';
import { navBarDiff, useNavBarStyle, useNavItemStyles } from '../commonNavStyles';
import Divider from '@mui/material/Divider';

// Map paragraphs to header tag within navbar
const NAV_MAPPING = { paragraphRegular: 'h4' };

export const GroupNavItem = React.memo(function GroupNavItem() {
  const { classes: navBarClasses } = useNavBarStyle();
  return (
    <div className={navBarClasses.group}>
      <div className={navBarClasses.groupDivider} />
    </div>
  );
});

const useNavItemPopover = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const store = useHoverStore();
  const isInteractive = useIsInteractive();
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<NodeJS.Timeout | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      store.setOverPop(false);
      setAnchorEl(null);
    }, 100);
  }, [cancelClose, store]);

  const handleMouseLeaveAnchor = useCallback(() => {
    if (store.getInteractive()) {
      store.setOverPop(true);
      scheduleClose();
    }
  }, [scheduleClose, store]);

  const handleEnterAnchor = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      cancelClose();
      setAnchorEl(e.currentTarget);
      store.setOverPop(false);
    },
    [cancelClose, store],
  );

  const handleClickItem = useCallback(() => {
    store.setOverPop(false);
    store.setOverBar(false);
    setAnchorEl(null);
  }, [store]);

  return {
    anchorEl,
    popoverRef,
    handleEnterAnchor,
    handleMouseLeaveAnchor,
    handleClickItem,
    scheduleClose,
    cancelClose,
    isInteractive,
  };
};

const PopperNavItem = React.memo(function PopperNavItem({
  label,
  tabName,
  path,
  featureFlag,
  handleClickItem,
  type,
  section,
}: {
  label: string;
  tabName: string;
  path: string;
  featureFlag?: FeatureFlagsKeys;
  handleClickItem: () => void;
  type: 'item' | 'divider';
  section?: string;
}) {
  const show = useAppSelector((state) =>
    featureFlag ? featureFlagsSelectors.getFeatureFlag(state, featureFlag) : true,
  );

  if (!show) {
    return null;
  }

  if (type === 'divider') {
    return <Divider sx={{ width: '100%' }} />;
  }

  const handleClick = () => {
    tracker.track(tracker.events.navMenuClick, { menuItem: label, section });
    handleClickItem();
  };

  return (
    <ButtonBase
      id={`nav-popover-item-${tabName}`}
      data-testid={`nav-popover-item-${tabName}`}
      onClick={handleClick}
      disableRipple
      disableTouchRipple
      component={NavLink}
      to={`${path}/${tabName}`}
      sx={{
        width: 1,
        height: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        backgroundColor: 'transparent',
        p: 1,
        px: 2.5,
        borderRadius: 0,
        color: 'text.primary',
        '&:hover': {
          color: 'primary.main',
        },
      }}
    >
      <Typography variant='paragraphRegular' sx={{ color: 'inherit', textTransform: 'capitalize' }}>
        {label}
      </Typography>
    </ButtonBase>
  );
});

export const MainNavItem = React.memo(function MainNavItem({
  nav,
  iconRef,
  section,
}: {
  nav?: FeaturePage;
  iconRef?: React.RefObject<HTMLDivElement | null>;
  section?: string;
}) {
  const { classes, cx } = useNavItemStyles();
  const {
    anchorEl,
    popoverRef,
    handleEnterAnchor,
    handleMouseLeaveAnchor,
    handleClickItem,
    scheduleClose,
    cancelClose,
    isInteractive,
  } = useNavItemPopover();
  const location = useLocation();
  const theme = useTheme();
  const isSelected = nav?.path === `/${location.pathname.split('/')[1]}`;
  const dispatch = useAppDispatch();
  const netWorthPageConfig = useAppSelector((state: RootState) =>
    reportConfigurationsSelectors.getReportConfigByUniqueIdentifierWeb(state, {
      getTabName: () => 'net-worth-page',
    }),
  );
  const showNav = useAppSelector((state) => {
    if (!nav) return false;
    const show =
      (nav.accessSelector ? nav.accessSelector(state) : true) &&
      (nav.businessEnabled ? entitlementsSelectors.getBusinessEnabled(state) : true) &&
      (nav.featureFlag ? featureFlagsSelectors.getFeatureFlag(state, nav.featureFlag) : true) &&
      (nav.featureFlagHide ? !featureFlagsSelectors.getFeatureFlag(state, nav.featureFlagHide) : true);

    return show;
  });

  if (!nav || !showNav) {
    return null;
  }
  const IconComponent = nav.icon;

  const ref = createRef<HTMLAnchorElement>();
  const aId = makeNavigationItemId(nav);
  const { path } = nav;

  return (
    <>
      <ButtonBase
        onClick={() => {
          tracker.track(tracker.events.navMenuClick, { menuItem: nav.title, section });
          if (nav.title === 'Spending Plan') {
            tracker.track(tracker.events.viewSpendingPlan, { initiator: 'navigation bar' });
          }
          if (nav.path === '/receipt-center') {
            trackReceiptEvent({ event: 'receiptCenterOpen', properties: { location: 'main-navigation' } });
          }
          if (nav.title === 'Net Worth' || nav.title === 'Dashboard') {
            if (nav.title === 'Net Worth') {
              tracker.track(tracker.events.viewNetWorthV2, { initiator: 'nav bar' });
            }

            if (netWorthPageConfig?.id) {
              dispatch(
                generatedReportsActions.generateReport({
                  location: netWorthPageConfig.id,
                  configuration: netWorthPageConfig,
                }),
              );
            }
          }
          handleClickItem();
        }}
        className={isSelected ? cx(classes.navMenuItem, classes.selectedItem) : classes.navMenuItem}
        to={nav.popover ? `${path}?tab=${nav.popover.default}` : path}
        component={NavLink}
        id={aId}
        ref={ref}
        aria-owns={nav.popover ? nav.popover.id : undefined}
        aria-haspopup={nav.popover ? 'true' : undefined}
        aria-describedby={nav.popover ? nav.popover.id : undefined}
        onMouseEnter={nav.popover ? handleEnterAnchor : undefined}
        onMouseLeave={nav.popover ? handleMouseLeaveAnchor : undefined}
      >
        {/* Below will keep the full nav item w/ darker background color when selected */}
        {/* IMPORTANT: this span is used in a css selector by the FutureTransactionGuidedPopover component */}
        <span ref={iconRef} className={classes.navBarIconSpan}>
          <IconComponent aria-label={nav.alt} className={classes.navIcon} />
        </span>
        <Stack
          flexDirection={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          flexGrow={1}
          flexBasis={'100%'}
        >
          <Stack flexDirection={'row'} alignItems={'center'}>
            <Typography
              variant='paragraphRegular'
              variantMapping={NAV_MAPPING}
              className={isSelected ? classes.selectedNavText : classes.navText}
            >
              {nav.title}
            </Typography>
            {nav.newChip ? (
              <Chip
                key={`new-feature-${nav.title}`}
                label={'New'}
                size='small'
                sx={{
                  backgroundColor: theme.palette.upcoming.light,
                  color: theme.palette.upcoming.dark,
                  marginLeft: theme.spacing(1),
                }}
              />
            ) : null}
          </Stack>
          {nav.popover ? <KeyboardArrowRight className={classes.navIcon} /> : null}
        </Stack>
      </ButtonBase>
      {nav.popover ? (
        <Popper
          ref={popoverRef}
          onMouseLeave={scheduleClose}
          onMouseEnter={cancelClose}
          disablePortal={false}
          container={() => document.body}
          open={!!anchorEl}
          anchorEl={anchorEl}
          id={nav.popover.id}
          placement='right-start'
          modifiers={[
            { name: 'offset', options: { offset: [-26, isInteractive ? -18 : -18 + navBarDiff] } },
            { name: 'preventOverflow', options: { padding: 8, rootBoundary: 'viewport' } },
          ]}
          sx={{
            pointerEvents: 'none',
            zIndex: 2000,
          }}
        >
          <Paper elevation={16} sx={{ pointerEvents: 'auto', borderRadius: 2, m: 1.5 }}>
            <Stack sx={{ minWidth: 155, alignItems: 'flex-start', pt: 1, pb: 1 }}>
              {nav.popover.pages.map((page) => (
                <PopperNavItem
                  key={page.tabName}
                  tabName={page.tabName}
                  label={page.label}
                  type={page.type}
                  path={path}
                  featureFlag={page.featureFlag}
                  handleClickItem={handleClickItem}
                  section='sales-and-billing'
                />
              ))}
            </Stack>
          </Paper>
        </Popper>
      ) : null}
    </>
  );
});

export const RefreshNavItem = React.memo(function RefreshNavItem({ closeDrawer }: { closeDrawer: () => void }) {
  const { classes } = useNavItemStyles();
  return (
    <RefreshButton closeHeader={closeDrawer} id='sync-arrow' classes={{ refreshDivButton: classes.navMenuItem }} />
  );
});
